<template>
  <div class="home">
    <div>
        <section>
            <div class="py-8 px-8">
                <div class="d-flex flex-wrap align-items-center justify-content-between">
                    <div class="col-12 col-lg-auto mb-3 mb-lg-0">
                        <div class="d-flex align-items-center">
                            <h4 class="mb-0 me-2">{{$t('contracts')}}</h4>
                        </div>
                    </div>
                    <div class="col-12 col-lg-auto d-flex align-items-center gap-2">
                        <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                            <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search')">
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="">

            <div class="px-8">
                <!-- Mobile action buttons -->
                <div class="d-flex d-md-none gap-2 mb-3">
                    <button @click="create" class="btn btn-sm btn-success flex-grow-1">
                        <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                        <span>{{$t('contract')}}</span>
                    </button>
                </div>

                <!-- Filters section (collapsible on mobile, always visible on desktop) -->
                <div class="row align-items-end g-2 mb-4" :class="{'d-none d-md-flex': !showFilters}">
                    
                    <!-- Filter Dropdown (Shopify Style) -->
                    <div class="col-12 col-md-auto">
                        <div class="dropdown">
                            <button class="btn btn-outline-secondary dropdown-toggle btn-sm" type="button" id="filterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                <AppIcon name="bi-funnel" class="bi bi-funnel" /> {{$t('filter')}}
                                <span v-if="activeFilters.length" class="badge bg-secondary ms-1">{{activeFilters.length}}</span>
                            </button>
                            <button v-if="activeFilters.length" class="btn btn-link btn-sm text-danger text-decoration-none ms-2" type="button" @click.stop="clearAllFilters">
                                <AppIcon name="bi-x-circle" class="bi bi-x-circle me-1" />{{$t('clear_all')}}
                            </button>
                            <div class="dropdown-menu p-3" aria-labelledby="filterDropdown" style="min-width: 300px;" @click.stop>
                                
                                <!-- Active Filters -->
                                <div v-if="activeFilters.length > 0" class="mb-3">
                                    <div v-for="(filter, index) in activeFilters" :key="index" class="mb-2">
                                        <label class="form-label small mb-1">{{$t(filter.key)}}</label>
                                        <div class="input-group input-group-sm">
                                            <!-- Select type filter (e.g. status) -->
                                            <select v-if="filter.type === 'select'" v-model="filter.value" class="form-select form-select-sm">
                                                <option value="">{{$t('all')}}</option>
                                                <option v-for="opt in filter.options" :key="opt.value" :value="opt.value">{{$t(opt.label)}}</option>
                                            </select>
                                            <!-- Date type filter -->
                                            <input v-else-if="filter.type === 'date'" type="date" v-model="filter.value" class="form-control form-control-sm">
                                            <!-- Text type filter -->
                                            <input v-else type="text" v-model="filter.value" class="form-control form-control-sm" :placeholder="$t(filter.key)">
                                            <button class="btn btn-outline-danger" type="button" @click="removeFilter(index)">
                                                <AppIcon name="bi-trash" class="bi bi-trash" />
                                            </button>
                                        </div>
                                    </div>
                                    <hr class="dropdown-divider">
                                </div>

                                <!-- Add Filter Options -->
                                <div class="d-grid gap-2">
                                    <button v-for="filter in selectableFilters" :key="filter.key" 
                                            class="btn btn-sm btn-outline-primary text-start" 
                                            @click="addFilter(filter)">
                                        <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t(filter.key)}}
                                    </button>
                                </div>

                                <!-- Apply and Clear -->
                                <div class="d-flex justify-content-between align-items-center mt-3">
                                    <button class="btn btn-sm btn-success" @click="applyFilters">
                                        {{$t('apply')}}
                                    </button>
                                    <button v-if="activeFilters.length > 0" class="btn btn-link btn-sm text-decoration-none text-danger p-0" @click="clearAllFilters">
                                        {{$t('clear_all')}}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Spacer for desktop -->
                    <div class="col d-none d-md-block"></div>
                    
                    <!-- Date picker -->
                    <div class="col-12 col-md-3 col-lg-2">
                        <label class="form-label small d-md-none mb-1">{{$t('filter_by_date')}}</label>
                        <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm">
                            <option value="">{{$t('filter_by_date')}}</option>
                            <option value="today">{{$t('today')}}</option>
                            <option value="yesterday">{{$t('yesterday')}}</option>
                            <option value="this_week">{{$t('this_week')}}</option>
                            <option value="last_week">{{$t('last_week')}}</option>
                            <option value="this_month">{{$t('this_month')}}</option>
                            <option value="custom">{{$t('custom_date')}}</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-3 col-lg-2" v-if="dateRangeOption === 'custom'">
                        <label class="form-label small d-md-none mb-1">{{$t('custom_date')}}</label>
                        <date-picker :clearable="true" :editable="false" class="w-100" v-model="range" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
                    </div>

                    <div class="col-md-auto d-none d-md-flex gap-2">
                        <button @click="create" class="btn btn-sm btn-success">
                            <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                            <span>{{$t('add_contract')}}</span>
                        </button>
                    </div>
                </div>
                <div class="page-table-container mb-6 bg-white rounded shadow">

                    <!-- table -->
                    <div class="table-responsive">
                        <table class="table mb-0 table-borderless table-striped small">
                            <thead>
                                <tr class="text-secondary">
                                    <th class="pt-4 pb-3 px-6" style="width: 40px;">
                                        <div class="form-check mb-0">
                                            <input class="form-check-input" type="checkbox" :checked="areAllSelected" @change="toggleSelectAll">
                                        </div>
                                    </th>
                                    <sortable-table-header field="contract_number" label="contract_number" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getContracts)" />
                                    <sortable-table-header field="title" label="title" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getContracts)" />
                                    <th class="pt-4 pb-3 px-6">{{$t('linked_entity')}}</th>
                                    <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getContracts)" />
                                    <sortable-table-header field="amount" label="amount" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getContracts)" />
                                    <sortable-table-header field="start_date" label="start_date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getContracts)" />
                                    <sortable-table-header field="end_date" label="end_date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getContracts)" />
                                    <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="contract in contracts" :key="contract.id" :class="{'table-active': contract.selected}">
                                    <td class="py-5 px-6">
                                        <div class="form-check mb-0">
                                            <input class="form-check-input" type="checkbox" v-model="contract.selected" @change="changeSelectedItem">
                                        </div>
                                    </td>
                                    <td class="py-5 px-6">
                                        <router-link :to="`/contracts/${contract.id}`"><b>{{contract.contract_number}}</b></router-link>
                                    </td>
                                    <td class="py-5 px-6">{{contract.title}}</td>
                                    <td class="py-5 px-6">
                                        <span v-if="contract.type === 'employee' && contract.employee">{{contract.employee.name}}</span>
                                        <router-link v-else-if="contract.type === 'client' && contract.client" :to="`/clients/${contract.client.id}`">
                                            {{contract.client.name}}
                                        </router-link>
                                        <span v-else-if="contract.type === 'supplier' && contract.supplier">{{contract.supplier.name}}</span>
                                        <span v-else>-</span>
                                    </td>
                                    <td class="py-5 px-6">
                                        <div v-if="helper.checkAccess('edit_contracts') != -1" class="dropdown">
                                            <button
                                                :id="`contract-status-${contract.id}`"
                                                type="button"
                                                class="badge border-0 dropdown-toggle"
                                                :class="getContractStatusBadge(contract.status)"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                                :disabled="statusUpdatingContractId === contract.id"
                                            >
                                                <span v-if="statusUpdatingContractId === contract.id" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                {{$t(contract.status)}}
                                            </button>
                                            <ul class="dropdown-menu shadow contract-status-menu" :aria-labelledby="`contract-status-${contract.id}`">
                                                <li v-for="status in contractStatuses" :key="status">
                                                    <button
                                                        type="button"
                                                        class="dropdown-item d-flex align-items-center justify-content-between gap-2"
                                                        :class="{ active: contract.status === status }"
                                                        @click="changeContractStatus(contract, status)"
                                                    >
                                                        <span>{{$t(status)}}</span>
                                                        <AppIcon name="bi-check-lg" v-if="contract.status === status" class="bi bi-check-lg" />
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                        <span v-else class="badge" :class="getContractStatusBadge(contract.status)">{{$t(contract.status)}}</span>
                                    </td>
                                    <td class="py-5 px-6">{{formatMoney(contract.amount)}}</td>
                                    <td class="py-5 px-6">{{formatDate(contract.start_date)}}</td>
                                    <td class="py-5 px-6">{{contract.end_date ? formatDate(contract.end_date) : '-'}}</td>
                                    <td class="py-5 px-6">
                                        <button @click="viewContract(contract)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary  btn-xs" :title="$t('view_with_template')">
                                            <AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf me-1" /> {{$t('view')}}
                                        </button>
                                        <button @click="goDetails(contract.id)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                                            <AppIcon name="bi-eye" class="bi bi-eye me-1" /> {{$t('details')}}
                                        </button>
                                        <button @click="editContract(contract)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                                            <AppIcon name="bi-pencil" class="bi bi-pencil me-1" style="font-size:12px" /> {{$t('edit')}}
                                        </button>
                                        <button @click="deleteRow(contract)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                                            <AppIcon name="bi-trash" class="bi bi-trash me-1" style="font-size:12px" /> {{$t('delete')}}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="count > contracts.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
                            <compact-pagination :pages="pagination" :current-page="page" @change="getContracts" />
                        </div>
                    </div>

                    <table-loading-bar v-if="loading" class="page-table-state text-center" />
                    <div v-if="!loading && contracts.length === 0" class="page-table-state text-center py-5 my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
                        <AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text text-muted" style="font-size: 3rem;" />
                        <h4 class="mt-3">{{ $t('no_contracts_found') }}</h4>
                        <p class="text-muted">{{ $t('contracts_description') }}</p>
                        <button class="btn btn-outline-primary mt-2" @click="create">
                            {{ $t('add_contract') }}
                        </button>
                    </div>
                </div>

                <div class="col-sm-7">
                    {{$t('show')}}
                    <select v-model="per_page" class="form-select mb-3" @change="getContracts(1)" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    {{$t('entities')}}
                </div>
            </div>
        </section>
    </div>

    <!-- Bulk Action Bar -->
    <div v-if="isSelectedItem" class="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow rounded d-flex align-items-center gap-3" style="z-index: 1050; border: 1px solid #dee2e6; min-width: 300px; justify-content: space-between;">
        <div class="d-flex align-items-center">
            <span class="fw-bold me-2">{{ selectedCount }}</span>
            <span class="text-muted">{{$t('selected')}}</span>
        </div>
        <div class="d-flex gap-2">
            <button class="btn btn-danger btn-sm d-flex align-items-center" @click="deleteSelected">
                <AppIcon name="bi-trash" class="bi bi-trash me-1" /> {{$t('delete')}}
            </button>
        </div>
    </div>

    <AddEditContractModal ref="addEditModal" :contractData="selectedContract" @saved="onContractSaved" />
    
    <ContractTemplateModal 
        v-if="viewModalIsOpen" 
        @modalClosed="viewModalIsOpen = false" 
        modalId="view-contract-modal" 
        :isModalOpen="viewModalIsOpen"  
        :data="viewingContract"
    />



  </div>
</template>

<script>
import helper from '../../helper'
import AddEditContractModal from '../../components/contract/AddEditContractModal.vue'
import ContractTemplateModal from '../../components/contract/ContractTemplateModal.vue'
import sortableList from '../../mixins/sortableList'

export default {
  name: 'Contract',
  persistedFilterState: {
    key: 'contracts',
    fields: ['search', 'activeFilters', 'dateRangeOption', 'range', 'start', 'end', 'per_page', 'sortField', 'sortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  components: {
    AddEditContractModal,
    ContractTemplateModal
  },
  data(){
    return {
        search: '',
        contracts: [],
        loading: false,
        per_page: 25,
        page: 1,
        count: 0,
        pagination: [],
        searchTimeout: null,
        selectedContract: null,
        statusUpdatingContractId: null,
        contractStatuses: ['active', 'pending', 'expired', 'terminated'],
        isSelectedItem: false,
        viewModalIsOpen: false,
        viewingContract: null,
        // Filters
        showFilters: false,
        activeFilters: [],
        availableFilters: [
            { 
                key: 'status', 
                type: 'select', 
                options: [
                    { value: 'active', label: 'active' },
                    { value: 'pending', label: 'pending' },
                    { value: 'expired', label: 'expired' },
                    { value: 'terminated', label: 'terminated' }
                ] 
            },
            { 
                key: 'type', 
                type: 'select', 
                options: [
                    { value: 'client', label: 'client' },
                    { value: 'supplier', label: 'supplier' },
                    { value: 'employee', label: 'employee' }
                ] 
            },
            { key: 'client', type: 'text' },
            { key: 'title', type: 'text' },
            { key: 'start_date', type: 'date' },
            { key: 'end_date', type: 'date' }
        ],
        dateRangeOption: '',
        range: [],
        start: null,
        end: null
    }
  },
  computed: {
    helper() {
        return helper;
    },
    areAllSelected() {
        return this.contracts.length > 0 && this.contracts.every(item => item.selected);
    },
    selectedCount() {
        return this.contracts.filter(item => item.selected).length;
    },
    selectableFilters() {
        return this.availableFilters.filter(f => !this.isFilterActive(f.key));
    }
  },
  watch: {
    search() {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(() => {
            this.getContracts()
        }, 500)
    },
    range(){
        if(this.range[0] == null){
            this.start = null
            this.end = null
            this.getContracts()
        }else if(this.range.length && this.range[0] && this.range[1]){
            this.start = new Date(this.range[0]).toISOString().split('T')[0]
            this.end = new Date(this.range[1]).toISOString().split('T')[0]
            this.getContracts()
        }else{
            this.contracts = []
        }
    }
  },
  mounted(){
    window.mixpanel.track("contract");
    
    //check permissions
    if(helper.checkAccess('view_contracts') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_contracts') == -2) return this.$router.push({path : '/subscribe'})

    this.getContracts()
  },
  methods: {
    formatMoney(number) { return helper.formatMoney(number);},
    formatDate(date) { return helper.formatDate(date) },
    create(){
        this.selectedContract = null;
        this.$refs.addEditModal.show('general', false);
    },
    editContract(contract) {
        this.selectedContract = contract;
        this.$refs.addEditModal.show('content', false);
    },
    viewContract(contract) {
        this.viewingContract = contract;
        this.viewModalIsOpen = true;
    },
    onContractSaved(data) {
        if (!data.is_template) {
            this.getContracts(this.page);
        }
    },
    getContractStatusBadge(status) {
        if (status === 'sent' || status === 'viewed') return 'bg-info';
        if (status === 'signed' || status === 'active') return 'bg-success';
        if (status === 'declined' || status === 'expired' || status === 'terminated') return 'bg-danger';
        if (status === 'pending') return 'bg-warning text-dark';
        return 'bg-secondary';
    },
    async changeContractStatus(contract, status) {
        if (this.statusUpdatingContractId === contract.id || contract.status === status) return;

        const toggle = document.getElementById(`contract-status-${contract.id}`);
        if (toggle) {
            // eslint-disable-next-line no-undef
            const dropdown = bootstrap.Dropdown.getInstance(toggle);
            if (dropdown) dropdown.hide();
        }

        const previousStatus = contract.status;
        this.statusUpdatingContractId = contract.id;
        this.$set(contract, 'status', status);

        try {
            await helper.customPatch(
                `${process.env.VUE_APP_API}/contracts/${contract.id}`,
                { status },
                { showToast: true }
            );
        } catch (error) {
            this.$set(contract, 'status', previousStatus);
        } finally {
            await this.getContracts(this.page);
            this.statusUpdatingContractId = null;
        }
    },

    goDetails(id){ this.$router.push({path: `/contracts/${id}`}) },
    async getContracts(page){
        try{
            this.loading = true
            this.page = page ? page : 1
            
            // Build query params
            const params = new URLSearchParams();
            params.append('page', this.page);
            params.append('per_page', this.per_page);
            params.append('is_template', 'false');
            this.appendSortParams(params);

            if (this.start) {
                params.append('start', this.start);
                params.append('end', this.end);
            }

            if (this.search && this.search.length > 0) {
                params.append('search', this.search);
            }

            // Add Shopify-style active filters
            for (const filter of this.activeFilters) {
                if (filter.value && filter.value.toString().trim() !== '') {
                    params.append(filter.key, filter.value);
                }
            }

            let url = `${process.env.VUE_APP_API}/contracts?${params.toString()}`;

            const response = await helper.customGet(url)
            this.contracts = response.data.data
            
            for(let i = 0 ; i < this.contracts.length ; i++){
                this.$set(this.contracts[i], 'selected', false)
            }

            this.count = response.data.count
            if(this.per_page != response.data.per_page){
                this.per_page = response.data.per_page
            }

            //pagination
            this.pagination = []
            let MaxPagination = Math.ceil(this.count / this.per_page )
            for(let i = 1 ; i <= MaxPagination ;i++){
                this.pagination.push(i)
            }

            this.loading = false
        }catch(error){
            this.loading = false
            this.$swal.fire({
                icon: 'error',
                title: this.$t('error'),
                text: error.message || error
            })
        }

    },
    toggleSelectAll() {
        const selectAll = !this.areAllSelected;
        this.contracts.forEach(contract => {
            contract.selected = selectAll;
        });
        this.changeSelectedItem();
    },
    changeSelectedItem() {
        this.isSelectedItem = this.contracts.find(item => item.selected === true ) ? true : false
    },
    async deleteRow(row){
        this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: this.$t('yes_delete_it')
        }).then(async (result) => {
            if (result.isConfirmed) {
                await helper.customDelete(`${process.env.VUE_APP_API}/contracts/${row.id}`)
                this.contracts = helper.withoutDeletedRecords(this.contracts, row.id)
                this.count = Math.max(0, this.count - 1)
            }
        })
    },
    addFilter(filter) {
        if (!this.isFilterActive(filter.key)) {
            const newFilter = { 
                key: filter.key, 
                value: '' 
            };
            // Carry over type and options for select-type filters
            if (filter.type) {
                newFilter.type = filter.type;
                newFilter.options = filter.options;
            }
            this.activeFilters.push(newFilter);
        }
    },
    removeFilter(index) {
        this.activeFilters.splice(index, 1);
        this.getContracts();
    },
    isFilterActive(key) {
        return this.activeFilters.some(f => f.key === key);
    },
    applyFilters() {
        this.getContracts();
    },
    clearAllFilters() {
        this.activeFilters = [];
        this.getContracts();
    },
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        // All dates - clear range
        this.range = []
        this.start = null
        this.end = null
        this.getContracts()
      } else if (this.dateRangeOption === 'today') {
        const start = new Date()
        const end = new Date()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'yesterday') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24)
        end.setTime(end.getTime() - 3600 * 1000 * 24)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_week') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'last_week') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 14)
        end.setTime(end.getTime() - 3600 * 1000 * 24 * 7)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_month') {
        const start = new Date()
        const end = new Date()
        start.setDate(1)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'custom') {
        // Don't set range yet, wait for user to pick dates
        this.range = []
      }
    },

    async deleteSelected() {
        const selectedIds = this.contracts.filter(c => c.selected).map(c => c.id);
        if (selectedIds.length === 0) return;

        const result = await this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text: this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: this.$t('yes_delete_it')
        });

        if (result.isConfirmed) {
            try {
                this.loading = true;
                const results = await Promise.allSettled(selectedIds.map(id => helper.customDelete(`${process.env.VUE_APP_API}/contracts/${id}`)));
                const deletedIds = helper.fulfilledRecordIds(selectedIds, results);
                this.loading = false;
                this.contracts = helper.withoutDeletedRecords(this.contracts, deletedIds);
                this.count = Math.max(0, this.count - deletedIds.length);
                this.isSelectedItem = false;
                if (deletedIds.length !== selectedIds.length) throw results.find(result => result.status === 'rejected').reason;
            } catch (error) {
                this.loading = false;
                alert(error);
            }
        }
    }
  }
}
</script>

<style scoped>
    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }

    .contract-status-menu {
        min-width: max-content;
        padding: 2px 0;
    }

    .contract-status-menu .dropdown-item {
        padding: 3px 8px;
        font-size: 12px;
    }
</style>
